/**
 * One standing order as the accounting system holds it - the `funds/financial-data/{id}/debits`
 * row. The order header lives on the movement itself; charges become movement details only after
 * the bank has collected them, which is why «תנועות עתידיות» projects the next one from here.
 *
 * `paymentMethod` is «בנקאי» for a bank order and «אשראי» for a credit-card one the office charges
 * itself (not a Nedarim order). Several dates the server could not fill arrive as "now" (an old
 * contract the Angular board relies on); `endsAt` and `lastChargeAt` are the honest nullable copies
 * added for the projection, and the HAT-1135 fields below are nullable from the start.
 */
interface Debit {
  debitId: number;
  donationId: number;
  /** The fund the order collects for (HAT-10024): the by-donor list spans funds and reads it off each row. */
  fundId: number;
  /** The fund's name, on the by-donor list only; null on the fund's own list. */
  fundName?: string | null;
  contactId: number;
  firstName: string;
  lastName: string;
  monthlyAmount: number;
  isActive: boolean;
  status: string | null;
  billingDayOfMonth: number;
  lastPaymentDate: string;
  numberOfPayments: number;
  countCharged: number;
  countRejected: number;
  amountCharged: number;
  amountRejected: number;
  paymentMethod: string;
  billingAccount: string | null;
  startDate: string;
  endDate: string;
  firstBillingDate: string;
  lastBillingDate: string;
  /** When the order ends, or null for an open-ended one. */
  endsAt?: string | null;
  /** Value date of the most recent collected charge, or null before the first. */
  lastChargeAt?: string | null;
  /** The bank's reason on the most recent return, or "" when none was. */
  rejectionReasonExample?: string | null;
  /** Bank, branch and account of a bank order, separately (HAT-1135); null on a credit one. */
  bank?: string | null;
  branch?: string | null;
  account?: string | null;
  accountHolder?: string | null;
  /** Date of the most recent return the bank sent back, or null when none was. */
  lastRejectionAt?: string | null;
  /** When the office last changed `status`. */
  statusDate?: string | null;
}

/** One charge or return of an APT standing order - `debits/{movementId}/history` (HAT-1135). */
export interface DebitHistoryItem {
  /** Value date of the collection. */
  date: string;
  /**
   * The collection's amount, positive - also when the bank returned it. Negative only on a return
   * the server could not put on any collection, which then stands as a row of its own.
   */
  amount: number;
  /** Returned: the bank sent this collection back (the return is put on it by the server). */
  kind: 'Charged' | 'Returned';
  /** When the bank sent it back. */
  returnedAt?: string | null;
  receiptId?: number | null;
  reason?: string | null;
}

export const DEBIT_METHOD_BANK = 'בנקאי';
export const DEBIT_METHOD_CREDIT = 'אשראי';

export default Debit;
